import validator from 'validator';
import { z } from 'zod';
import {
  TWINGATE_NETWORK_DISPLAY_NAME_MAX_LENGTH,
  TWINGATE_NETWORK_SLUG_MAX_LENGTH,
  TWINGATE_NETWORK_SLUG_PATTERN,
} from './connector-manager.constants';

const IPV4_CIDR_PATTERN =
  /^(?:(?:25[0-5]|2[0-4]\d|1?\d?\d)\.){3}(?:25[0-5]|2[0-4]\d|1?\d?\d)\/(?:[0-9]|[1-2]\d|3[0-2])$/;

const isIpv4Address = (value: string): boolean => validator.isIP(value, 4);

const isDnsHostname = (value: string): boolean =>
  validator.isFQDN(value, { require_tld: false });

export const TailscaleAdvertisedRouteSchema = z
  .string()
  .regex(IPV4_CIDR_PATTERN, 'Must be an IPv4 CIDR such as 10.0.0.0/24');

export const TailscaleHostAliasSchema = z.object({
  hostname: z
    .string()
    .min(1)
    .max(253)
    .refine(isDnsHostname, 'Must be a DNS hostname'),
  ip: z.string().refine(isIpv4Address, 'Must be an IPv4 address'),
});

export const TailscaleSplitDnsSchema = z.object({
  nameservers: z
    .array(z.string().refine(isIpv4Address, 'Must be an IPv4 address'))
    .min(1)
    .max(10),
  domains: z
    .array(
      z.string().min(1).max(253).refine(isDnsHostname, 'Must be a DNS domain'),
    )
    .min(1)
    .max(50),
});

const TailscaleProxyUrlSchema = z
  .string()
  .min(1)
  .regex(
    /^(https?|socks5):\/\/.+/i,
    'Must be an http://, https://, or socks5:// URL',
  );

export const TailscaleEgressProxySchema = z.object({
  url: TailscaleProxyUrlSchema,
  noProxy: z.string().nullable().optional(),
});

export const TailscaleNetworkInfoSchema = z.object({
  id: z.string(),
  slug: z.string(),
  displayName: z.string(),
  siteId: z.number().int(),
  isDefault: z.boolean(),
  isActive: z.boolean(),
  hasOverlappingCidrs: z.boolean(),
  advertisedRoutes: z.array(z.string()),
  advertised4via6Routes: z.array(z.string()),
  connectorStatus: z.enum(['PENDING', 'ACTIVE', 'REVOKED', 'OFFLINE']),
  lastSeenAt: z.string().nullable(),
  hostAliases: z.array(TailscaleHostAliasSchema),
  splitDns: TailscaleSplitDnsSchema.nullable().default(null),
  egressProxy: TailscaleEgressProxySchema.nullable(),
  resourceProxy: TailscaleEgressProxySchema.nullable().default(null),
  transport: z.literal('internal-access').default('internal-access'),
});

export const ListTailscaleNetworksResponseSchema = z.object({
  result: z.array(TailscaleNetworkInfoSchema),
});

export const CreateTailscaleNetworkParamsSchema = z.object({
  slug: z
    .string()
    .min(1)
    .max(TWINGATE_NETWORK_SLUG_MAX_LENGTH)
    .regex(TWINGATE_NETWORK_SLUG_PATTERN),
  displayName: z.string().min(1).max(TWINGATE_NETWORK_DISPLAY_NAME_MAX_LENGTH),
  advertisedRoutes: z.array(TailscaleAdvertisedRouteSchema).min(1),
  hostAliases: z.array(TailscaleHostAliasSchema).optional(),
  splitDns: TailscaleSplitDnsSchema.optional(),
  egressProxy: TailscaleEgressProxySchema.optional(),
  resourceProxy: TailscaleEgressProxySchema.optional(),
});

export const CreateTailscaleNetworkRequestSchema = z.object({
  params: CreateTailscaleNetworkParamsSchema,
});

export const TailscaleConnectorArtifactsSchema = z.object({
  composeYaml: z.string(),
  deploymentCommand: z.string(),
  advertisedRoutes: z.array(z.string()),
  advertised4via6Routes: z.array(z.string()),
  controlPlaneUrl: z.string(),
});

export const CreateTailscaleNetworkResponseSchema = z.object({
  result: z.object({
    network: TailscaleNetworkInfoSchema,
    artifacts: TailscaleConnectorArtifactsSchema,
  }),
});

export const GetTailscaleNetworkResponseSchema = z.object({
  result: TailscaleNetworkInfoSchema,
});

export const UpdateTailscaleNetworkParamsSchema = z.object({
  displayName: z
    .string()
    .min(1)
    .max(TWINGATE_NETWORK_DISPLAY_NAME_MAX_LENGTH)
    .optional(),
  advertisedRoutes: z.array(TailscaleAdvertisedRouteSchema).min(1).optional(),
  hostAliases: z.array(TailscaleHostAliasSchema).optional(),
  splitDns: TailscaleSplitDnsSchema.nullable().optional(),
  egressProxy: TailscaleEgressProxySchema.nullable().optional(),
  resourceProxy: TailscaleEgressProxySchema.nullable().optional(),
  isActive: z.boolean().optional(),
});

export const UpdateTailscaleNetworkRequestSchema = z.object({
  params: UpdateTailscaleNetworkParamsSchema,
});

export const UpdateTailscaleNetworkResponseSchema = z.object({
  result: TailscaleNetworkInfoSchema,
});

export const TailscaleNetworkIdPathParamsSchema = z.object({
  networkId: z.string(),
});

export const ProvisionTailscaleConnectorResponseSchema = z.object({
  result: TailscaleConnectorArtifactsSchema,
});
